/**
 * 场景卡目录(B2 §2.0)—— §1 卡片映射表 1:1 落码,按 hub 分组导出。
 *
 * 拍板(§3.1):卡片目录 = console 常量,不走后端下发;权限/计费仍由后端 gate
 * (moduleKey 显隐走 visibleByPerms,扣费在生成动作)。卡面文案逐字取自终稿
 * (docs/2026-07-21-爆款解析驱动-终稿.html 屏①③⑤⑦)。
 *
 * 铁律:有真出品才上卡 —— enabled 卡的 thumb(public/scene-cards/<key>.png)必须
 * 真实存在(catalog.test.ts 用 fs 锁);缺图卡 enabled:false;「即将」卡 soon:true
 * 无 route 无 handler。
 */

import {
  DH_VARIANT,
  SKU,
  imageVariant,
  label,
  type PriceBook,
} from "@/entities/pricing/model/priceBook";
import {
  EXPLOSIVE_FULL_JOB_CREDITS_APPROX,
  grouped,
  LONGIMAGE_ONECLICK_DEFAULT_TIER,
  perScreens,
  perImage,
  perImageBlocks,
  perSecond,
  POSTER_DEFAULT_TIER,
  REPORTER_PER_ARTICLE_LABEL,
  VIDEO_COMPOSE_CREDITS_CAP,
  VIDEO_COMPOSE_TYPICAL_RANGE,
  VIDEO_DEFAULT_CLIP_SECONDS,
  VIDEO_DEFAULT_RESOLUTION,
  VIDEO_PURPOSE_DEFAULT_CLIP_SECONDS,
} from "./model/creditRates";
import { VIDEO_WALL_PURPOSES, briefPurposeOf, videoSceneKeyOf } from "./model/briefPurpose";
import { TRYON_SCENE_KEY, type LongImageUsage } from "./model/presets";
import type { SceneCard } from "./model/types";

const thumb = (key: string) => `/scene-cards/${key}.jpg`;

/**
 * 卡面报价用的默认规格档 —— **与创建页的落地默认同源**,不是随手举的一个配置。
 *
 * ★ 2026-08-01 的 A6 修的就是「卡面报一个档、点进去落另一个档」:卡面写死 15 秒报 8280,
 *   创建页初始 durationSec 是 5 → 页内估算 3280,**差 2.52 倍**。所以这几个 key 必须跟着
 *   创建页的初始值走,谁改默认卡面自动跟着改。
 */
const POSTER_DEFAULT_VARIANT = imageVariant("openai", POSTER_DEFAULT_TIER);
/**
 * 长图用途卡(10 张 `longimage_*`)的卡面报价档 = **一键出图的落地默认档**(1K)。
 *
 * ★0927 K3(第十三批 D2)之前这里写死 2K,注释说「搭建器/驾驶舱默认 scale=2 → 2K」—— 那是
 *   一键路上线(0820)之前的口径。0823 批 C-2 起一键默认 1K(`ONECLICK_DEFAULT_TIER`),而用途卡
 *   点进去**默认选中的正是一键路**(`CanvasIntakeBox`:带用途 ⇒ path "oneclick")。于是卡面
 *   「默认 8 块 · 3 图块 ≈ 198」(3×66)对着页内「一键出图 · 约 28–308」(1K)、0927 演示机实扣
 *   1K 3 屏 84 —— 卡面高报 2.36 倍。现在与 `ONECLICK_DEFAULT_TIER` 同源
 *   (`LONGIMAGE_ONECLICK_DEFAULT_TIER`,features 侧从 entities 取值),parity 单测钉相等。
 * ★两个数的口径(电商详情卡为例,1K 单价 28)—— 不是同一个量,但档位必须是同一个:
 *   · 卡面「默认 8 块 · 按 3 屏计 · 84 积分起」(1003 B9 起按屏,此前按图块 3×28 只是数值巧合):
 *     `skeletonFor("goods")` 默认骨架 8 块,一键按屏结算,最优装箱 ⌈8/3⌉ = 3 屏 × 28 = 84,
 *     与 0927 实测那一单(3 屏 84)同值;是屏数下界,所以写「起」。
 *   · 页内「一键出图 · 约 28–308」:`longImageOneClickEstimate` 的**区间**,块数不取骨架而取
 *     `ONECLICK_BLOCK_RANGE.goods = [3, 11]`(块数跟用户给的料走):下界 ⌈3/3⌉ = 1 屏 × 28 = 28;
 *     上界**不除 3**(真装箱器看行密度,密块最坏一块一屏)= 11 × 28 = 308。
 *   跟海报张卡的落地默认(POSTER_DEFAULT_TIER)是两条线的两个量,不复用 POSTER_DEFAULT_VARIANT。
 */
const LONGIMAGE_ONECLICK_VARIANT = imageVariant("openai", LONGIMAGE_ONECLICK_DEFAULT_TIER);
/**
 * 横宽卡(poster_wide)的报价档 —— 它没有用途,不走一键默认路:落搭建器手搭/智能编排,
 * 默认 scale=2 → 2K(`useLongImageBuilder` 初始 scale 2 / ai `LongImageCanvas.scale` 缺省 2,
 * `longImageEstimate.tierOfScale`)。与用途卡不是同一条落地路,所以**不**复用上面那个常量;
 * 横宽自 0812 起在 POSTER_HIDDEN_CARD_KEYS 里不上墙。
 */
const LONGIMAGE_BUILDER_VARIANT = imageVariant("openai", "2K");
/** 视频创建页初始值:provider=seedance / model=doubao-seedance-2-0-fast / 720p(见 VideoCreatePage)。 */
const VIDEO_DEFAULT_VARIANT = `seedance20fast_${VIDEO_DEFAULT_RESOLUTION}`;

/**
 * 长图 10 用途卡文案(终稿屏③「长图工坊」;key = longimage_<usageKey> 与 sceneKey 同源)。
 *
 * `usageKey` = 搭建器的用途枚举(LongImageUsage);`usage` = 卡面「一句话用途」中文串。
 * 两者同名过一次,2026-07-23 加用途文案时拆开 —— 一个是路由参数,一个是给人读的。
 */
const LONG_IMAGE_CARDS: Array<{
  usageKey: LongImageUsage;
  title: string;
  usage: string;
  desc: string;
  tags: string[];
  /**
   * 该用途默认骨架的总块数 / 其中图块数 —— 卡面积分标的两个自变量(§8 定案)。
   *
   * ★为什么是字面量而不是 `import { SKELETON_ESTIMATE }`:那张表在 `features/poster`,
   * 从 `entities` 反向 import 会把整个长图区块模型拖进每一个卡目录消费者,层级也做反了。
   * 代价是这两个数会漂 —— 所以 `catalog.test.ts` 逐条对着 `skeletonFor` 真骨架断言:
   * 骨架改一块、这里没跟,单测立刻红。**积分数本身仍是算出来的**(`perScreens`),
   * 不是抄的(E0 纠偏红线:卡面与实扣差一个数量级 = 对客事故)。
   */
  blocks: number;
  imageBlocks: number;
}> = [
  {
    usageKey: "goods",
    title: "电商详情长图",
    usage: "把卖点铺成一整页,顾客滑到底",
    desc: "卖点分层 · 参数 / 对比条 / 背书全取 KB 真数据。",
    tags: ["KB 真数据", "图块 AI"],
    blocks: 8,
    imageBlocks: 3,
  },
  {
    usageKey: "corp",
    title: "企业介绍长图",
    usage: "一页讲清公司是谁、做过什么",
    desc: "愿景 / 发展历程时间轴 / 资质荣誉 / 核心团队。",
    tags: ["时间轴", "资质荣誉"],
    blocks: 7,
    imageBlocks: 2,
  },
  {
    usageKey: "event",
    title: "活动海报长图",
    usage: "活动信息 + 报名码,一张搞定",
    desc: "主视觉 / 亮点 / 时间地点 / 参与流程,自带报名二维码块。",
    tags: ["报名二维码", "图块 AI"],
    blocks: 6,
    imageBlocks: 2,
  },
  {
    usageKey: "review",
    title: "产品测评长图",
    usage: "摆事实讲数据,帮顾客下决心",
    desc: "维度评分 / 实测数据 / 优缺点双栏 / 购买 CTA。",
    tags: ["维度评分", "实测数据"],
    blocks: 6,
    imageBlocks: 1,
  },
  {
    usageKey: "report",
    title: "数据战报长图",
    usage: "把经营数据做成能发出去的图",
    desc: "统计大牌 / 趋势折线 / 结构饼图 / 销量榜单,真数据可视化。",
    tags: ["折线 · 饼图", "AI 不编数字"],
    blocks: 6,
    imageBlocks: 1,
  },
  {
    usageKey: "hire",
    title: "招聘长图",
    usage: "岗位 + 待遇 + 投递码,一页发出去",
    desc: "我们是谁 / 岗位列表 / 薪资福利 / 成长路径 + 投递二维码。",
    tags: ["投递二维码", "信息卡"],
    blocks: 6,
    imageBlocks: 1,
  },
  {
    usageKey: "menu",
    title: "菜单价目单",
    usage: "改一次价,全渠道菜单跟着变",
    desc: "分类价目 / 套餐组合取商品库,改价即同步 + 点单二维码。",
    tags: ["价格同步", "点单二维码"],
    blocks: 6,
    imageBlocks: 2,
  },
  {
    usageKey: "tutorial",
    title: "教程攻略长图",
    usage: "分步骤教用户怎么用",
    desc: "封面 / 编号步骤 / 小贴士引用 / 成品展示 / 关注 CTA。",
    tags: ["编号列表", "引用"],
    blocks: 6,
    imageBlocks: 2,
  },
  {
    usageKey: "rank",
    title: "榜单长图",
    usage: "用真实销量排个榜",
    desc: "TOP 榜取真实销量 / KB 指标,注明数据来源。",
    tags: ["榜单", "真销量"],
    blocks: 5,
    imageBlocks: 1,
  },
  {
    usageKey: "social",
    title: "社交种草长图",
    usage: "写成公众号 / 小红书能发的图文",
    desc: "金句开场 / 正文段落 / 引用 / 关注 CTA,公众号 · 小红书排版。",
    tags: ["金句", "图块 AI"],
    blocks: 6,
    imageBlocks: 2,
  },
];

/** 创意设计中心(AI海报 hub,终稿屏③)—— 商品出图 3 卡。 */
export const POSTER_GOODS_CARDS: SceneCard[] = [
  {
    key: "poster_single",
    hub: "poster",
    title: "商品主图",
    usage: "给商品拍一张能直接上架的主图",
    desc: "单张出图,产品图锚定防变形;标题 / 卖点文字排版可控。",
    shape: "square",
    tags: ["AI 出图", "产品图打底"],
    costLabel: (b) =>
      label(
        b.unit(SKU.posterImage, POSTER_DEFAULT_VARIANT),
        (c) => `${perImage(c)}(${POSTER_DEFAULT_TIER} 默认档)`
      ),
    route: "/ai-haibao/create",
    preset: { kind: "poster_single" },
    thumb: thumb("poster_single"),
    grade: "live",
    enabled: true,
  },
  {
    key: "poster_group",
    hub: "poster",
    title: "爆点组图",
    usage: "一个卖点一张图,做成轮播",
    desc: "每张讲一个爆点,不是同一张的变体;小红书轮播 / 详情组图。",
    shape: "carousel",
    tags: ["内容驱动", "可从爆款开始"],
    costLabel: (b) =>
      label(b.unit(SKU.posterImage, POSTER_DEFAULT_VARIANT), (c) => `${perImage(c)} · N 张按张计`),
    route: "/ai-haibao/create",
    preset: { kind: "poster_group", groupPoints: [] },
    thumb: thumb("poster_group"),
    grade: "live",
    enabled: true,
  },
  {
    key: "poster_multiratio",
    hub: "poster",
    title: "全渠道套图",
    usage: "一次出三个比例,各平台同步发",
    desc: "同一内容一次出三比例,抖音 / 朋友圈 / 官网同步投放。",
    shape: "ratios",
    tags: ["一次三比例", "多平台合规"],
    costLabel: (b) =>
      label(
        b.unit(SKU.posterImage, POSTER_DEFAULT_VARIANT),
        (c) => `3 张约 ${grouped(3 * c)} 积分`
      ),
    route: "/ai-haibao/create",
    preset: { kind: "poster_multiratio", ratios: ["9:16", "1:1", "16:9"] },
    thumb: thumb("poster_multiratio"),
    grade: "live",
    enabled: true,
  },
];

/** 长图工坊 10 用途卡(同一搭建器,切用途 = 换整套默认区块)。 */
export const POSTER_LONGIMAGE_CARDS: SceneCard[] = LONG_IMAGE_CARDS.map((c) => ({
  key: `longimage_${c.usageKey}`,
  hub: "poster",
  title: c.title,
  usage: c.usage,
  desc: c.desc,
  shape: "long",
  tags: c.tags,
  // 「仅图块计费」说不出多少钱,而长图是舱内最贵的一条路径(§2 问题五)。
  // 骨架固定 → 块数/图块数编译期已知 → 卡面就能给出真实量级,页内估算器再跟着用户改的块数走。
  costLabel: (b) =>
    label(b.unit(SKU.longImage, LONGIMAGE_ONECLICK_VARIANT), (per) =>
      perScreens(c.blocks, per, LONGIMAGE_ONECLICK_DEFAULT_TIER)
    ),
  route: "/ai-haibao/detail-long",
  preset: { kind: "longimage", usage: c.usageKey },
  thumb: thumb(`longimage_${c.usageKey}`),
  grade: "new",
  enabled: true,
}));

/**
 * 横宽宽图(C3 上卡)—— 同一个长图搭建器的横宽模式:版式模板决定分区、8 尺寸预设定画幅。
 * 默认落「店铺 banner 1920×640 · 左图右文」,进页后版式/尺寸都能改。
 */
export const POSTER_WIDE_CARD: SceneCard = {
  key: "poster_wide",
  hub: "poster",
  title: "横宽宽图",
  usage: "出横幅、封面、大屏那种宽图",
  desc: "版式模板决定分区(左图右文 / 上下 / 居中叠字 / 三分),图区 AI 出图,文 / 数据区模板渲染。",
  shape: "wide",
  tags: ["单帧分区", "8 尺寸预设"],
  // 横宽块数随版式变(居中叠字 1 区 / 左图右文·上下 2 区 / 三分 3 区),所以只标图块数;
  // 卡面默认版式 lr = 1 图块。数值由 catalog.test.ts 对着 wideSkeletonFor 锁。
  costLabel: (b) =>
    label(b.unit(SKU.longImage, LONGIMAGE_BUILDER_VARIANT), (per) => perImageBlocks(1, per)),
  route: "/ai-haibao/detail-long",
  preset: { kind: "poster_wide", wideLayout: "lr", widePreset: "banner" },
  thumb: thumb("poster_wide"),
  grade: "new",
  enabled: true,
};

/**
 * AI 模特换衣(2026-08-12 拍板上卡,方案 docs/2026-08-12-ai-tryon-PLAN.md §2)。
 *
 * ★为什么单独一个导出而不是塞进 `POSTER_GOODS_CARDS`:那三张是**同一个创建页**的三种预设
 *   (route 全是 `/ai-haibao/create`),而换衣是自己的三步页、自己的引擎(百炼 aitryon-plus)、
 *   自己的单价。塞进去会让「商品出图 3 卡」这个名字与内容对不上,归组仍由
 *   `posterPurpose.ts` 那张表决定(它才是卡墙口径),两件事互不干扰。
 * ★`sceneKey` 显式登记:卡 key 是素材文件名口径(`tryon.jpg` / `tryon.webp`),
 *   打标 key 跟后端模块名走 `ai_tryon`(见 `TRYON_SCENE_KEY` 头注)。
 * ★素材已到位(2026-08-12 当天自产,占位态随即收口 —— 反向锁的设计就是逼这一步):
 *   三皮肤主角图用 gpt-image-2 按 `poster_single` 那套画风逐条对齐生成
 *   (standard 真人+紫框 / cyber 深底霓虹机器人+青框 / comic 白底 3D 卡通+粉框);
 *   卡面样图是**我们自己生成的模特照**,不是从上游文档样例里拿的(那批有授权问题),
 *   同时它也是 P1「预置模特库」的第一张验证图 —— 单人全身正面、手不交叉,恰好是
 *   aitryon 对模特图的硬要求。
 */
export const POSTER_TRYON_CARD: SceneCard = {
  key: "tryon",
  sceneKey: TRYON_SCENE_KEY,
  hub: "poster",
  title: "AI 模特换衣",
  usage: "让模特穿上你的衣服拍商品图",
  desc: "传一张模特照 + 平铺服装图,专项试衣模型只换衣服 —— 布料纹理与 Logo 照原样保真。",
  shape: "square",
  tags: ["服装保真", "上装 / 下装", "不用请模特"],
  // 卡面标算出来:0.50 元/张 ⇒ 500 积分(creditRates 单源,不写字面量)
  costLabel: (b) => label(b.unit(SKU.tryon, "default"), (c) => perImage(c)),
  route: "/ai-haibao/tryon",
  preset: { kind: "tryon" },
  thumb: thumb("tryon"),
  grade: "new",
  enabled: true,
};

/**
 * 「自由创作」卡的价签 —— **直发路**(POST /video/tasks,单镜 T2V,不经编排、不出台本图)。
 *
 * ★B2 #9(0927,K4 演示机实测):此前这张卡与 8 张目的卡共用「5 秒 720P 约 565 积分(默认档,含 1 张
 *   台本图)」—— 直发路根本没有台本图,实扣 475(video.render 一行,无 video.frame)。卡面多报 90。
 *   价 = 每秒价 × 默认秒数,来自价目表,取不到整条返空串(卡面报价与实扣对不上是对客红线,E0)。
 */
export function videoFreeClipCostLabel(b: PriceBook): string {
  const perSec = b.unit(SKU.video, VIDEO_DEFAULT_VARIANT);
  if (perSec == null) return "";
  return `${VIDEO_DEFAULT_CLIP_SECONDS} 秒 ${VIDEO_DEFAULT_RESOLUTION.toUpperCase()} 约 ${grouped(perSec * VIDEO_DEFAULT_CLIP_SECONDS)} 积分(直发,不含编排与台本图)`;
}

/**
 * 照片数字人卡(dh_photo)价签。1019 照片省流版永久下架,照片档只剩生动版:生动版的规格档由
 * api price-variants 现给(前端不存映射),卡面这里不报数,只说按秒计。
 */
export function dhPhotoCostLabel(): string {
  return "生动版按秒计费,进页看报价";
}

/**
 * 8 张目的卡的价签 —— **编排路**:「AI 智能编排」一次(token 计费,通常区间 + 封顶)+ 成片按秒
 * (默认「短 · 15s」档)。台本图按镜数另计,卡上不报数(镜数编排后才知道)。
 * 编排区间与默认秒数都在 creditRates 单源;每秒价来自价目表,取不到整条返空串。
 */
export function videoPurposeCostLabel(b: PriceBook): string {
  const perSec = b.unit(SKU.video, VIDEO_DEFAULT_VARIANT);
  if (perSec == null) return "";
  const [lo, hi] = VIDEO_COMPOSE_TYPICAL_RANGE;
  const clip = grouped(perSec * VIDEO_PURPOSE_DEFAULT_CLIP_SECONDS);
  // 1006:编排按 token 计费、无下限只有封顶 —— 报「通常 + 最高」,不报成「约 下限~上限」(见 creditRates 头注)。
  return `编排通常 ${grouped(lo)}~${grouped(hi)}(最高 ${grouped(VIDEO_COMPOSE_CREDITS_CAP)})+ 成片按秒(${VIDEO_PURPOSE_DEFAULT_CLIP_SECONDS} 秒 ${VIDEO_DEFAULT_RESOLUTION.toUpperCase()} 约 ${clip})`;
}

/**
 * 视频 8 目的卡(2026-09-12 老板拍板 hub 三段式重排:4 组卡**拆回** 8 档一卡)。
 *
 * ═══ 8 → 4 → 8:为什么拆回来 ═══════════════════════════════════════════════════
 * 2026-08-10 收成 4 组卡是为了让墙面短一点,代价是卡面标题变成了组名(「品牌实力」
 * 「活动招聘」「获客种草」)—— 那三个词在引擎里**不存在**,用户点「活动招聘」落地的是
 * 「活动预告」,想拍招聘片还得进页再换一次档。0912 重排把墙面按**目的**分两段
 * (带货与获客 / 品牌与组织),每段 4 张,标题回到引擎口径 label:点哪张就预置哪档,
 * 页内不用再换。引擎 playbook / sceneKey / 历史打标一个字没动。
 *
 * ★卡面数据仍是 `BRIEF_PURPOSES` 单一源现算,顺序由 `VIDEO_WALL_PURPOSES` 给。
 * ★`desc` 留空是**有意的**:0912 卡面形制 = 标题 + 副题(usage)+ 叙事弧 chip + 价签,
 *   不出第三行说明 —— `desc` 那句本就是 `arc` 的长版(见 briefPurpose.ts arc 头注),
 *   两句都印在 254px 宽的卡上是同一件事说两遍。`SceneCapCard` 对空 desc 整行不渲染。
 * ★`enabled` 恒 true:8 档引擎早已全部支持;三皮肤主角图与真产物样图两套素材也都齐
 *   (`/scene-figures/<skin>/video_<key>.webp` + `/scene-cards/video_<key>.jpg`),
 *   catalog.test.ts 那两把 fs 锁在管。
 * TODO(定妆样图):现有 8 张 `/scene-cards/video_*.jpg` 是 4 组卡时代留下的真产物,
 *   重出一批定妆样图后逐卡替换(文件名不变,换图即生效)。
 */
const PURPOSE_CARDS: SceneCard[] = VIDEO_WALL_PURPOSES.map((label) => {
  const p = briefPurposeOf(label)!;
  const key = videoSceneKeyOf(label);
  return {
    key,
    hub: "video",
    title: p.label,
    usage: p.usage,
    desc: "",
    shape: "video",
    // 卡面只留叙事弧那一颗 chip(能力 tags 降级进创建页);拷一份可变数组:
    // 单一源那份是 `as const` 冻结的(SceneCard.tags 要 string[])。
    tags: [p.arc],
    costLabel: videoPurposeCostLabel,
    route: "/ai-video/create",
    // map 参数 `label` 已是 BriefPurpose 字面量并集;`p.label` 是 string(宽),用窄的那个。
    preset: { kind: "video", purpose: label },
    thumb: thumb(key),
    grade: "new",
    enabled: true,
  };
});

/** 视频创作 hub(0912:8 目的卡 + 追爆/仿拍 2 导流卡)。 */
export const VIDEO_CARDS: SceneCard[] = [
  ...PURPOSE_CARDS,
  // 追爆/仿拍按设计就是导流卡(卡面文案=贴链接→蓝图→同款成片),route 落情报枢纽
  // 而非创建页 —— 不是漏配 preset(hub→hub 深链无 state 铁律)。
  // ★2026-09-12 起 hub 上**不再各摆一条**:两张卡落的是同一个页面(/ai-explosive)、
  //   同一件事(贴链接拆结构),来源扁条合成一条「从爆款开始」。catalog 两条**都留着** ——
  //   它们是 `sceneLabelOf` / 场景筛的打标口径,删掉会让历史任务的「来自哪张卡」失效。
  //   入口合并 ≠ 打标合并,这是两层。
  {
    key: "video_trend_follow",
    hub: "video",
    funnel: true, // 来源卡(不是本舱产物)→ 不进场景计数,见 types.ts funnel

    title: "一键追爆",
    usage: "贴个链接,套出自家同款片",
    desc: "贴链接拆爆款,蓝图一键套自家产品出同款成片。",
    shape: "video",
    tags: ["从爆款开始", "蓝图"],
    costLabel: () => `取片+解析 ${EXPLOSIVE_FULL_JOB_CREDITS_APPROX} 积分 + 成片按秒计`,
    route: "/ai-explosive",
    thumb: thumb("video_trend_follow"),
    grade: "live",
    enabled: true,
    moduleKey: "ai_explosive_parse",
  },
  {
    key: "video_reenact",
    hub: "video",
    funnel: true, // 来源卡(不是本舱产物)→ 不进场景计数,见 types.ts funnel

    title: "爆款仿拍",
    usage: "照着爆款的镜头节奏拍一条",
    desc: "逐镜对齐原片结构仿拍,只学结构不搬素材。",
    shape: "video",
    tags: ["仿拍", "只学结构"],
    costLabel: () => `取片+解析 ${EXPLOSIVE_FULL_JOB_CREDITS_APPROX} 积分 + 成片按秒计`,
    route: "/ai-explosive",
    thumb: thumb("video_reenact"),
    grade: "live",
    enabled: true,
    moduleKey: "ai_explosive_parse",
  },
];

/** 数字分身工作室快捷卡(终稿屏⑦;B5 接线上卡)。 */
export const DH_CARDS: SceneCard[] = [
  {
    // 2026-08-13 用户拍板改名:这条线只吃**视频**形象(照片在 doCreate 就被 `视频档需选视频形象`
    // 拒掉,console 上传口也已收窄)。叫「口播成片」时它读起来像是整个分身舱的总入口,
    // 与隔壁「照片数字人」不成对;改名后两张卡按**原料**分,点进去收什么一眼就懂。
    key: "dh_talking",
    hub: "dh",
    title: "视频数字人",
    usage: "用你的真人视频换台词",
    desc: "传一段真人出镜视频,保留原片画面只换台词对口型,可用原声。",
    shape: "avatar",
    tags: ["真人视频", "可用原声", "插播画面标记"],
    // ★单价钉这条线**真走的**管线(videoretalk=80/秒)。原来写的是 flagship(照片旗舰档 500/秒)
    // —— 那是隔壁照片线的价,IA 拆线时漏改,卡面报价比实扣高 6.25 倍。
    costLabel: (b) =>
      label(b.unit(SKU.dhRender, DH_VARIANT.video), (c) => `${perSecond(c)}(视频换口播)`),
    route: "/ai-digital-human/talking",
    preset: { kind: "dh_talking" },
    thumb: thumb("dh_talking"),
    grade: "mod",
    enabled: true,
  },
  {
    // E1:引擎(ai resolve.py avatarSource=upload_photo)与创建页上传口自 A 阶段就在,
    // 这张卡补的是「一张照片就能开口」这个场景的入口(1019 起照片档只剩生动版)。
    key: "dh_photo",
    hub: "dh",
    title: "照片数字人",
    // 不另造「不限时长」这类我们没实证过的承诺。
    usage: "用一张照片让人开口说话",
    // 卡面文案/报价读生动版(1019 照片省流版永久下架),进页落地默认档也是生动版。
    desc: "一张正脸照就能开口:上传照片 + 肖像授权,选音色写文案,单条最长 20 秒。",
    shape: "avatar",
    tags: ["一张照片", "肖像授权", "带表情动作"],
    costLabel: () => dhPhotoCostLabel(),
    route: "/ai-digital-human/photo",
    preset: { kind: "dh_photo" },
    thumb: thumb("dh_photo"),
    grade: "new",
    enabled: true,
  },
  {
    key: "dh_tts",
    hub: "dh",
    title: "文生语音",
    usage: "把文案转成能直接用的配音",
    desc: "只出声音不合成视频,配音 / 电话 / 播报便宜一档。",
    shape: "audio",
    tags: ["纯音频", "克隆音色"],
    costLabel: (b) => label(b.unit(SKU.dhRender, DH_VARIANT.tts), (c) => perSecond(c)),
    route: "/ai-digital-human/tts",
    preset: { kind: "dh_tts" },
    thumb: thumb("dh_tts"),
    grade: "new",
    enabled: true,
  },
  {
    key: "dh_voice_clone",
    hub: "dh",
    title: "声纹克隆",
    usage: "朗读 10 秒以上,留下专属音色",
    // ★0926:克隆按次扣(dh.voice_clone,0908 起 10 积分/次),旧文案「克隆不扣 · 配额内」是错的。
    desc: "朗读 10 秒以上,录到 19 秒自动停,即建专属音色;克隆成功按次扣积分。",
    shape: "audio",
    tags: ["10s 起克隆", "试听"],
    costLabel: (b) => label(b.unit(SKU.dhVoiceClone, "default"), (c) => `${c} 积分 / 次`),
    route: "/ai-digital-human/voices",
    thumb: thumb("dh_voice_clone"),
    grade: "live",
    enabled: true,
  },
  {
    key: "dh_from_explosive",
    hub: "dh",
    title: "从爆款开始",
    usage: "拆爆款的口播公式,换成自家的",
    desc: "拆爆款口播公式,套自家产品逐句重写脚本。",
    shape: "avatar",
    tags: ["口播公式", "蓝图"],
    costLabel: () => `取片+解析 ${EXPLOSIVE_FULL_JOB_CREDITS_APPROX} 积分 + 合成按秒计`,
    route: "/ai-explosive",
    thumb: thumb("dh_from_explosive"),
    grade: "live",
    enabled: true,
    moduleKey: "ai_explosive_parse",
    // 2026-08-07 用户拍板:从分身舱卡墙下架(能力一条不删 —— 爆款页那侧「送去数字人」、
    // 蓝图交接、blueprintId 随提交上送全保留)。放回来 = 删掉这一行。
    hidden: true,
  },
];

/**
 * 官媒舱 6 卡(R2-T3;5 体裁 live + 发布中心 soon)。
 * key = REPORTER_GENRE_SCENE_KEY 的值(presets 单源,与任务打标 / works 场景筛同一张表)。
 * (2026-07-27 挂小人批:三皮肤主角图已产齐,figurePending 已按反向锁收口切真图。)
 */
export const REPORTER_CARDS: SceneCard[] = [
  {
    key: "reporter_news",
    hub: "reporter",
    title: "企业新闻",
    usage: "把企业动作写成通发新闻稿",
    desc: "发布会、签约、上新、里程碑,写成能对外通发的新闻稿。",
    shape: "article",
    tags: ["新闻事件", "企业动作", "背景信息"],
    costLabel: () => REPORTER_PER_ARTICLE_LABEL,
    route: "/ai-jizhe/create",
    preset: { kind: "reporter", genre: "COMPANY_NEWS" },
    thumb: thumb("reporter_news"),
    grade: "new",
    enabled: true,
  },
  {
    key: "reporter_product",
    hub: "reporter",
    title: "产品软文",
    usage: "讲清产品痛点卖点,不夸大",
    desc: "讲清楚产品解决什么痛点,有转化意识但不夸大;可挂产品树归属。",
    shape: "article",
    tags: ["目标用户", "核心卖点", "可挂产品树"],
    costLabel: () => REPORTER_PER_ARTICLE_LABEL,
    route: "/ai-jizhe/create",
    preset: { kind: "reporter", genre: "PRODUCT_COPY" },
    thumb: thumb("reporter_product"),
    grade: "new",
    enabled: true,
  },
  {
    key: "reporter_xhs",
    hub: "reporter",
    // 2026-08-10 维度转正:卡=体裁、渠道后置出口 —— 显示名脱渠道(genre 代码 XIAOHONGSHU 不动)。
    title: "种草笔记",
    usage: "写体验视角的种草笔记",
    desc: "体验视角的种草文,个人感受与普遍效果分得清清楚楚。",
    shape: "article",
    tags: ["主题", "使用体验", "适合人群"],
    costLabel: () => REPORTER_PER_ARTICLE_LABEL,
    route: "/ai-jizhe/create",
    preset: { kind: "reporter", genre: "XIAOHONGSHU" },
    thumb: thumb("reporter_xhs"),
    grade: "new",
    enabled: true,
  },
  {
    key: "reporter_event",
    hub: "reporter",
    title: "活动通稿",
    usage: "把活动写成预告或回顾通稿",
    desc: "活动预告 / 回顾通稿,时间地点嘉宾只用你给的,绝不脑补。",
    shape: "article",
    tags: ["时间地点", "主办嘉宾", "不脑补"],
    costLabel: () => REPORTER_PER_ARTICLE_LABEL,
    route: "/ai-jizhe/create",
    preset: { kind: "reporter", genre: "EVENT_RELEASE" },
    thumb: thumb("reporter_event"),
    grade: "new",
    enabled: true,
  },
  {
    key: "reporter_brand",
    hub: "reporter",
    title: "品牌介绍",
    usage: "公司团队创始人,选一种写",
    desc: "公司简介 / 团队 / 创始人 / 品牌故事四种子类,进创建页再选。",
    shape: "article",
    tags: ["组织名称", "主营业务", "核心能力"],
    costLabel: () => REPORTER_PER_ARTICLE_LABEL,
    route: "/ai-jizhe/create",
    preset: { kind: "reporter", genre: "BRAND_PROFILE" },
    thumb: thumb("reporter_brand"),
    grade: "new",
    enabled: true,
  },
  {
    key: "reporter_qa",
    hub: "reporter",
    title: "GEO 问答文",
    usage: "写会被 AI 搜索引用的问答文",
    desc: "小标题就是用户会问的问题,答案先给结论 —— 面向 AI 搜索引擎的 GEO 核心体裁。",
    shape: "article",
    tags: ["问答长尾", "先答后述", "GEO"],
    costLabel: () => REPORTER_PER_ARTICLE_LABEL,
    route: "/ai-jizhe/create",
    preset: { kind: "reporter", genre: "QA_ARTICLE" },
    // 2026-08-10 素材已到,占位态收口切真图:样图=真 QA 成稿截图(锐光美学篇,PASS 100);
    // 三皮肤主角图=各皮肤角色底图 gpt-image-2 高保真 i2i 换道具(问答气泡+问号)。
    thumb: thumb("reporter_qa"),
    grade: "new",
    enabled: true,
  },
  {
    key: "reporter_publish",
    hub: "reporter",
    title: "发布中心",
    usage: "多平台账号一键发出去",
    desc: "搜狐号 / 公众号 / 头条多账号一键发布 —— 落地后同时点亮全媒运营舱。",
    shape: "article",
    tags: ["账号矩阵", "发布日历"],
    costLabel: () => "规划中",
    route: null,
    thumb: null,
    grade: "soon",
    enabled: false,
    soon: true,
  },
];

export const SCENE_CARDS: SceneCard[] = [
  ...POSTER_GOODS_CARDS,
  ...POSTER_LONGIMAGE_CARDS,
  POSTER_WIDE_CARD,
  POSTER_TRYON_CARD,
  ...VIDEO_CARDS,
  ...DH_CARDS,
  ...REPORTER_CARDS,
];

/**
 * B6:sceneKey → 场景展示名(列表卡「来自哪张卡」标签用)。场景名单一源在本 catalog ——
 * 各线列表不另立文案表(platformLabel/moduleLabel 同范式)。未知/历史 NULL → null(不渲染标签)。
 */
export function sceneLabelOf(sceneKey?: string | null): string | null {
  return cardOfSceneKey(sceneKey)?.title ?? null;
}

/**
 * sceneKey(**后端打标口径**)→ 卡。绝大多数卡打标 key 就是卡 key,少数派在
 * `SceneCard.sceneKey` 里登记(换衣:卡 key `tryon` / 打标 `ai_tryon`)。
 *
 * ★两条查法缺一不可:只按 `key` 查,登记过的少数派会静默查不到(标签退成线名);
 *   只按 `sceneKey` 查,没登记的绝大多数卡全查不到。
 */
export function cardOfSceneKey(sceneKey?: string | null): SceneCard | null {
  if (!sceneKey) return null;
  return SCENE_CARDS.find((c) => c.key === sceneKey || c.sceneKey === sceneKey) ?? null;
}

/** 卡 → 它的任务打标 key(缺省即卡 key)。场景筛下推 `?sceneKey=` 必须用这个,别用 `c.key`。 */
export const taskSceneKeyOf = (card: SceneCard): string => card.sceneKey ?? card.key;

/**
 * 2026-08-01:sceneKey → **用户在卡面读到的那句目的**(`usage` 原文)。
 *
 * ★发给归整层的就是这一句。与 `ratioComposition` 同一条纪律:同一句话既给用户看、
 *   也进 prompt,**不另写第二份** —— 写第二份必漂,而漂的后果是界面说 A、模型收到 B。
 * ★不做 key→版面法则的映射表:表写死会锁过头(2026-07-31 爆点组图版式锚第一版把
 *   「镜头距离」写进不许动名单,两张图 pHash 汉明只有 6)。这里只负责把那句话取出来,
 *   版面交给模型判;真跑发现某条用途不稳时再针对那一条补结构化约束。
 * ★未知 / 历史 key → null(前端据此整个不发字段),与 `sceneLabelOf` 同口径。
 */
export function sceneUsageOf(sceneKey?: string | null): string | null {
  return cardOfSceneKey(sceneKey)?.usage ?? null;
}
